import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";

const AuthCtx = createContext(null);
const CACHE_KEY = "amds_user_cache_v1";

function readCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function writeCache(u) {
  try {
    if (u && typeof u === "object") window.localStorage.setItem(CACHE_KEY, JSON.stringify(u));
    else window.localStorage.removeItem(CACHE_KEY);
  } catch {}
}

export function AuthProvider({ children }) {
  // Start with the cached user — Safari paints the app immediately
  // instead of showing a blank screen while /api/auth/me round-trips.
  // We still re-validate against the server on mount.
  const [user, setUser] = useState(readCache());

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      writeCache(data);
    } catch {
      setUser(false);
      writeCache(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      writeCache(data);
      return { ok: true, user: data };
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Structured 403 from the email-verification gate. Surface a distinct
      // shape so the Login page can render the "check your email" screen
      // with a Resend button instead of a flat error toast.
      if (e.response?.status === 403 && detail && typeof detail === "object" && detail.code === "email_not_verified") {
        return { ok: false, needsVerification: true, email: detail.email || email, error: detail.message };
      }
      return { ok: false, error: formatApiErrorDetail(detail) || e.message };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      // Phase 2 — register now returns {requires_verification:true} instead
      // of an authenticated user. We deliberately DON'T setUser so /home
      // stays gated until the user clicks the verification link and signs in.
      if (data && data.requires_verification) {
        return { ok: true, requiresVerification: true, email: data.email };
      }
      // Backwards-compat path (shouldn't trigger post-Phase-2).
      setUser(data);
      writeCache(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
    writeCache(null);
  };

  return (
    <AuthCtx.Provider value={{ user, login, register, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
